import {
  Typography,
  Card,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Divider,
  Alert,
  Spin,
  Tabs,
  Switch,
  Space,
  Tag,
  Select,
  Input,
} from 'antd';
import { SaveOutlined, HolderOutlined } from '@ant-design/icons';
import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { settingsApi } from '@/api/settings.api';
import type { SystemSettings } from '@/api/settings.api';
import { appVersionsApi } from '@/api/app-versions.api';
import type { AppVersion, AdConfig, SportConfigItem } from '@/api/app-versions.api';

const { Title, Text } = Typography;

/** 광고 노출 순서 프리셋 — order 배열을 사용자 친화적 선택지로 매핑 */
const AD_ORDER_PRESETS: Record<string, { label: string; order: string[] }> = {
  cauly_first: { label: 'Cauly 우선 → AdMob 폴백', order: ['cauly', 'admob'] },
  admob_first: { label: 'AdMob 우선 → Cauly 폴백', order: ['admob', 'cauly'] },
  cauly_only: { label: 'Cauly만', order: ['cauly'] },
  admob_only: { label: 'AdMob만', order: ['admob'] },
};

function presetKeyFromOrder(order: string[] | undefined): string {
  const joined = (order ?? []).join(',');
  if (joined === 'cauly,admob') return 'cauly_first';
  if (joined === 'admob,cauly') return 'admob_first';
  if (joined === 'cauly') return 'cauly_only';
  return 'admob_only';
}

/** 플랫폼·버전별 광고 네트워크(순서 + 키) 편집기 */
function AdConfigEditor({
  version,
  onSave,
  saving,
}: {
  version: AppVersion;
  onSave: (cfg: AdConfig) => void;
  saving: boolean;
}) {
  const cfg = version.adConfig;
  const [preset, setPreset] = useState(presetKeyFromOrder(cfg?.order));
  const [caulyCode, setCaulyCode] = useState(cfg?.cauly?.code ?? '');
  const [admobNativeId, setAdmobNativeId] = useState(cfg?.admob?.nativeId ?? '');

  const handleSave = () => {
    const next: AdConfig = { order: AD_ORDER_PRESETS[preset].order };
    if (caulyCode.trim()) next.cauly = { code: caulyCode.trim() };
    if (admobNativeId.trim()) next.admob = { nativeId: admobNativeId.trim() };
    onSave(next);
  };

  return (
    <>
      <Divider style={{ margin: '12px 0' }} />
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Text type="secondary">광고 네트워크 우선순위</Text>
        <Select
          value={preset}
          onChange={setPreset}
          style={{ width: '100%' }}
          options={Object.entries(AD_ORDER_PRESETS).map(([k, v]) => ({
            value: k,
            label: v.label,
          }))}
        />
        <Input
          addonBefore="Cauly 코드"
          value={caulyCode}
          onChange={(e) => setCaulyCode(e.target.value)}
          placeholder={version.platform === 'IOS' ? 'd8NrBoPi' : 'Qqe2fxy7'}
        />
        <Input
          addonBefore="AdMob ID"
          value={admobNativeId}
          onChange={(e) => setAdmobNativeId(e.target.value)}
          placeholder="ca-app-pub-..."
        />
        <Button type="primary" size="small" loading={saving} onClick={handleSave}>
          광고 설정 저장
        </Button>
      </Space>
    </>
  );
}

/** 앱 하드코딩 기본 종목 목록 — sportConfig null 상태의 기본값과 동일해야 함 */
const DEFAULT_SPORTS: SportConfigItem[] = [
  { value: 'GOLF', label: '골프', visible: true },
  { value: 'BILLIARDS_4BALL', label: '당구 4구', visible: true },
  { value: 'BILLIARDS_3CUSHION', label: '당구 3쿠션', visible: true },
  { value: 'TENNIS', label: '테니스', visible: true },
  { value: 'TABLE_TENNIS', label: '탁구', visible: true },
  { value: 'BADMINTON', label: '배드민턴', visible: true },
  { value: 'BOWLING', label: '볼링', visible: true },
  { value: 'ROCK_PAPER_SCISSORS', label: '가위바위보', visible: true },
  { value: 'ARM_WRESTLING', label: '팔씨름', visible: true },
];

/** 종목 관리 편집기 — 드래그 순서 변경 + 노출 토글 + 표시명 편집 */
function SportConfigEditor({
  versions,
  onSave,
  saving,
}: {
  versions: AppVersion[];
  onSave: (cfg: SportConfigItem[]) => void;
  saving: boolean;
}) {
  // IOS row 기준으로 sportConfig 초기화 (없으면 기본값 사용)
  const iosVersion = versions.find((v) => v.platform === 'IOS');
  const initialConfig = iosVersion?.sportConfig ?? DEFAULT_SPORTS;

  const [items, setItems] = useState<SportConfigItem[]>(() => {
    // 서버에 없는 종목은 기본값에서 추가 (하위호환)
    const configMap = new Map(initialConfig.map((c) => [c.value, c]));
    const merged = initialConfig.slice();
    for (const def of DEFAULT_SPORTS) {
      if (!configMap.has(def.value)) {
        merged.push(def);
      }
    }
    return merged;
  });

  const dragIndex = useRef<number | null>(null);

  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === index) return;
    const next = items.slice();
    const [moved] = next.splice(dragIndex.current, 1);
    next.splice(index, 0, moved);
    dragIndex.current = index;
    setItems(next);
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
  };

  const toggleVisible = (index: number) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, visible: !item.visible } : item)),
    );
  };

  const changeLabel = (index: number, label: string) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, label } : item)));
  };

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Alert
        message="종목 관리"
        description="행을 드래그해 순서를 변경하고, 토글로 노출 여부를 제어합니다. 저장 시 iOS·Android 양쪽에 동일하게 적용됩니다."
        type="info"
        showIcon
        style={{ marginBottom: 8 }}
      />
      {items.map((item, index) => (
        <div
          key={item.value}
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 12px',
            background: item.visible ? '#1a1a2e' : '#111',
            border: '1px solid #333',
            borderRadius: 8,
            cursor: 'grab',
            opacity: item.visible ? 1 : 0.5,
          }}
        >
          <HolderOutlined style={{ color: '#666', flexShrink: 0 }} />
          <Tag color="default" style={{ minWidth: 40, textAlign: 'center', flexShrink: 0 }}>
            {index + 1}
          </Tag>
          <Tag color="blue" style={{ flexShrink: 0, fontFamily: 'monospace', fontSize: 11 }}>
            {item.value}
          </Tag>
          <Input
            size="small"
            value={item.label}
            onChange={(e) => changeLabel(index, e.target.value)}
            style={{ flex: 1, minWidth: 80 }}
          />
          <Switch
            size="small"
            checked={item.visible}
            onChange={() => toggleVisible(index)}
            checkedChildren="노출"
            unCheckedChildren="숨김"
          />
        </div>
      ))}
      <Button
        type="primary"
        icon={<SaveOutlined />}
        loading={saving}
        onClick={() => onSave(items)}
        style={{ marginTop: 4 }}
      >
        종목 설정 저장
      </Button>
    </Space>
  );
}

export function SystemSettingsPage() {
  const queryClient = useQueryClient();
  const [kForm] = Form.useForm();
  const [tierForm] = Form.useForm();
  const [matchForm] = Form.useForm();
  const [rankingForm] = Form.useForm();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings', 'system'],
    queryFn: settingsApi.getSystemSettings,
  });

  // 설정 데이터 로드 후 폼 초기값 설정
  if (settings) {
    kForm.setFieldsValue(settings.kFactor);
    tierForm.setFieldsValue(settings.tierThresholds);
    matchForm.setFieldsValue(settings.matchSettings);
    rankingForm.setFieldsValue(settings.rankingSettings);
  }

  const updateMutation = useMutation({
    mutationFn: (data: Partial<SystemSettings>) => settingsApi.updateSystemSettings(data),
    onSuccess: () => {
      message.success('설정이 저장되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['settings', 'system'] });
    },
    onError: () => message.error('설정 저장에 실패했습니다.'),
  });

  // 앱 버전(광고 토글 등)
  const { data: appVersions, isLoading: versionsLoading } = useQuery({
    queryKey: ['app-versions'],
    queryFn: appVersionsApi.list,
  });

  const adToggleMutation = useMutation({
    mutationFn: ({ id, showAd }: { id: string; showAd: boolean }) =>
      appVersionsApi.update(id, { showAd }),
    onSuccess: () => {
      message.success('광고 설정이 저장되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['app-versions'] });
    },
    onError: () => message.error('광고 설정 저장에 실패했습니다.'),
  });

  const adConfigMutation = useMutation({
    mutationFn: ({ id, adConfig }: { id: string; adConfig: AdConfig }) =>
      appVersionsApi.update(id, { adConfig }),
    onSuccess: () => {
      message.success('광고 네트워크 설정이 저장되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['app-versions'] });
    },
    onError: () => message.error('광고 네트워크 설정 저장에 실패했습니다.'),
  });

  const sportConfigMutation = useMutation({
    mutationFn: async (cfg: SportConfigItem[]) => {
      // IOS·ANDROID 양쪽에 동일하게 저장
      await Promise.all(
        (appVersions ?? []).map((v) => appVersionsApi.update(v.id, { sportConfig: cfg })),
      );
    },
    onSuccess: () => {
      message.success('종목 설정이 저장되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['app-versions'] });
    },
    onError: () => message.error('종목 설정 저장에 실패했습니다.'),
  });

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={4} style={{ marginBottom: 4 }}>
        시스템 설정
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
        K계수, 티어 기준, 매칭 설정 등 시스템 파라미터 관리
      </Text>

      <Alert
        message="설정 변경 주의사항"
        description="시스템 설정 변경은 즉시 서비스에 적용됩니다. 신중하게 변경해주세요."
        type="warning"
        showIcon
        style={{ marginBottom: 20 }}
      />

      <Tabs
        defaultActiveKey="kfactor"
        items={[
          {
            key: 'kfactor',
            label: 'ELO K계수',
            children: (
              <Card style={{ borderRadius: 8 }}>
                <Alert
                  message="K계수 설명"
                  description="K계수는 경기 결과에 따른 점수 변동폭을 결정합니다. 값이 클수록 점수 변동이 커집니다."
                  type="info"
                  showIcon
                  style={{ marginBottom: 20 }}
                />
                <Form
                  form={kForm}
                  layout="vertical"
                  initialValues={settings?.kFactor}
                  onFinish={(values) => updateMutation.mutate({ kFactor: values })}
                >
                  <Row gutter={24}>
                    <Col xs={24} sm={12} lg={6}>
                      <Form.Item
                        name="beginner"
                        label="입문 (첫 10게임)"
                        extra="빠른 점수 수렴"
                        rules={[{ required: true }, { type: 'number', min: 10, max: 80 }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={10} max={80} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Form.Item
                        name="intermediate"
                        label="중간 (11~30게임)"
                        extra="중간 수렴 단계"
                        rules={[{ required: true }, { type: 'number', min: 10, max: 60 }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={10} max={60} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Form.Item
                        name="standard"
                        label="표준 (31게임 이상)"
                        extra="안정화 단계"
                        rules={[{ required: true }, { type: 'number', min: 8, max: 40 }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={8} max={40} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Form.Item
                        name="platinum"
                        label="플래티넘 티어"
                        extra="고점수 변동 억제"
                        rules={[{ required: true }, { type: 'number', min: 4, max: 24 }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={4} max={24} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={updateMutation.isPending}
                  >
                    K계수 저장
                  </Button>
                </Form>
              </Card>
            ),
          },
          {
            key: 'tier',
            label: '티어 기준',
            children: (
              <Card style={{ borderRadius: 8 }}>
                <Alert
                  message="티어 점수 기준 (최솟값)"
                  description="각 티어의 최소 점수를 설정합니다. 브론즈 최솟값은 시스템 하한선입니다."
                  type="info"
                  showIcon
                  style={{ marginBottom: 20 }}
                />
                <Form
                  form={tierForm}
                  layout="vertical"
                  initialValues={settings?.tierThresholds}
                  onFinish={(values) => updateMutation.mutate({ tierThresholds: values })}
                >
                  <Row gutter={24}>
                    {[
                      { name: 'bronzeMin', label: '브론즈 최솟값', color: '#CD7F32' },
                      { name: 'silverMin', label: '실버 최솟값', color: '#C0C0C0' },
                      { name: 'goldMin', label: '골드 최솟값', color: '#FFD700' },
                      { name: 'platinumMin', label: '플래티넘 최솟값', color: '#888' },
                    ].map(({ name, label, color }) => (
                      <Col xs={24} sm={12} lg={6} key={name}>
                        <Form.Item
                          name={name}
                          label={<span style={{ color, fontWeight: 600 }}>{label}</span>}
                          rules={[{ required: true }, { type: 'number', min: 100 }]}
                        >
                          <InputNumber
                            style={{ width: '100%', borderColor: color }}
                            min={100}
                            max={3000}
                            step={50}
                            addonAfter="점"
                          />
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={updateMutation.isPending}
                  >
                    티어 기준 저장
                  </Button>
                </Form>
              </Card>
            ),
          },
          {
            key: 'match',
            label: '매칭 설정',
            children: (
              <Card style={{ borderRadius: 8 }}>
                <Form
                  form={matchForm}
                  layout="vertical"
                  initialValues={settings?.matchSettings}
                  onFinish={(values) => updateMutation.mutate({ matchSettings: values })}
                >
                  <Row gutter={24}>
                    <Col xs={24} sm={12} lg={8}>
                      <Form.Item
                        name="expirationHours"
                        label="매칭 요청 만료 시간"
                        extra="대기 중 매칭 요청 자동 만료 시간"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={1} max={72} addonAfter="시간" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <Form.Item
                        name="instantMatchWindowHours"
                        label="즉시 매칭 유효 시간"
                        extra="오늘 대결 요청 유효 시간"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={1} max={12} addonAfter="시간" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <Form.Item
                        name="defaultRadiusKm"
                        label="기본 매칭 반경"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={1} max={50} addonAfter="km" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <Form.Item
                        name="minRadiusKm"
                        label="최소 매칭 반경"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={1} max={10} addonAfter="km" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <Form.Item
                        name="maxRadiusKm"
                        label="최대 매칭 반경"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={10} max={100} addonAfter="km" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <Form.Item
                        name="cancelPenaltyThreshold"
                        label="취소 패널티 기준 (월)"
                        extra="월 N회 이상 취소 시 경고"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={1} max={10} addonAfter="회" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={updateMutation.isPending}
                  >
                    매칭 설정 저장
                  </Button>
                </Form>
              </Card>
            ),
          },
          {
            key: 'ads',
            label: '광고',
            children: (
              <Card style={{ borderRadius: 8 }}>
                <Alert
                  message="앱 광고 표시 토글"
                  description="플랫폼별로 네이티브 광고 표시 여부를 즉시 제어합니다. OFF일 경우 앱은 광고 로드 자체를 하지 않습니다. (앱이 다음 버전 체크 시점에 반영)"
                  type="info"
                  showIcon
                  style={{ marginBottom: 20 }}
                />
                {versionsLoading ? (
                  <Spin />
                ) : (
                  <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    {(appVersions ?? []).map((v) => (
                      <Card key={v.id} size="small">
                        <Row align="middle" justify="space-between">
                          <Col>
                            <Space size={12}>
                              <Tag color={v.platform === 'IOS' ? 'blue' : 'green'}>
                                {v.platform}
                              </Tag>
                              <Text>현재 버전: {v.latestVersion}</Text>
                            </Space>
                          </Col>
                          <Col>
                            <Space>
                              <Text type="secondary">광고 표시</Text>
                              <Switch
                                checked={v.showAd}
                                loading={adToggleMutation.isPending}
                                onChange={(checked) =>
                                  adToggleMutation.mutate({ id: v.id, showAd: checked })
                                }
                              />
                            </Space>
                          </Col>
                        </Row>
                        <AdConfigEditor
                          version={v}
                          saving={adConfigMutation.isPending}
                          onSave={(cfg) =>
                            adConfigMutation.mutate({ id: v.id, adConfig: cfg })
                          }
                        />
                      </Card>
                    ))}
                  </Space>
                )}
              </Card>
            ),
          },
          {
            key: 'sports',
            label: '종목 관리',
            children: (
              <Card style={{ borderRadius: 8 }}>
                {versionsLoading ? (
                  <Spin />
                ) : (
                  <SportConfigEditor
                    versions={appVersions ?? []}
                    saving={sportConfigMutation.isPending}
                    onSave={(cfg) => sportConfigMutation.mutate(cfg)}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'ranking',
            label: '랭킹 설정',
            children: (
              <Card style={{ borderRadius: 8 }}>
                <Form
                  form={rankingForm}
                  layout="vertical"
                  initialValues={settings?.rankingSettings}
                  onFinish={(values) => updateMutation.mutate({ rankingSettings: values })}
                >
                  <Row gutter={24}>
                    <Col xs={24} sm={12} lg={8}>
                      <Form.Item
                        name="minGamesForRanking"
                        label="핀 랭킹 최소 게임 수"
                        extra="랭킹 진입 조건"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={1} max={20} addonAfter="게임" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <Form.Item
                        name="nationalRankingMinGames"
                        label="전국 랭킹 최소 게임 수"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={5} max={50} addonAfter="게임" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <Form.Item
                        name="inactiveDaysThreshold"
                        label="비활성 랭킹 숨김 기간"
                        extra="N일 이상 비활성 시 랭킹 숨김"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={7} max={180} addonAfter="일" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Divider />
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={updateMutation.isPending}
                  >
                    랭킹 설정 저장
                  </Button>
                </Form>
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
}
